/**
 * Network API Client - extends BaseApiClient for network-related operations
 *
 * Supports multi-zone architecture by prefixing zone-scoped endpoints with /zones/{zoneId}
 */
import type { ExpectingRequestConfig } from './BaseApiClient';
import { BaseApiClient } from './BaseApiClient';
import { useZoneStore } from '../../store/zoneStore';

/**
 * Endpoints that should NOT be prefixed with zone ID
 * These are either zone management endpoints or global endpoints
 */
const ZONE_EXEMPT_PREFIXES = [
  '/zones',     // Zone management endpoints
  '/health',    // Health check
  '/auth',      // Authentication
];

class NetworkApiClient extends BaseApiClient {
  private static instance: NetworkApiClient;

  private constructor() {
    super();
  }

  public static getInstance(): NetworkApiClient {
    if (!NetworkApiClient.instance) {
      NetworkApiClient.instance = new NetworkApiClient();
    }
    return NetworkApiClient.instance;
  }

  /**
   * Gets the current active zone ID from the store
   */
  private getActiveZoneId(): string | null {
    return useZoneStore.getState().activeZoneId;
  }

  /**
   * Prepends zone prefix to the endpoint.
   * Zone-scoped endpoints become: /zones/{zoneId}/endpoint
   * Throws an error if no zone is selected - this is intentional to enforce proper zone selection.
   */
  private withZonePrefix(endpoint: string): string {
    // Check if endpoint is exempt from zone prefixing
    const isExempt = ZONE_EXEMPT_PREFIXES.some(prefix =>
      endpoint.startsWith(prefix)
    );

    if (isExempt) {
      return endpoint;
    }

    const zoneId = this.getActiveZoneId();
    if (!zoneId) {
      throw new Error(`[NetworkApiClient] Cannot make zone-scoped request to "${endpoint}" - no zone selected. Select a zone first.`);
    }

    // Prefix with /zones/{zoneId}
    return `/zones/${zoneId}${endpoint}`;
  }

  // Network data operations (zone-scoped)
  async getNetwork<T>(): Promise<T> {
    return this.get<T>(this.withZonePrefix('/network'));
  }

  async updateElement<T>(id: string, element: any): Promise<T> {
    return this.post<T>(this.withZonePrefix(`/network/element/${id}`), element);
  }

  // Settings operations — deliberately GLOBAL, not zone-scoped: system_settings
  // has no zone_id column (discriminated by environment only). The old
  // /zones/{id}/settings route silently ignored the zone and was removed
  // 2026-08 because it faked per-zone thresholds that don't exist.
  async getSettings<T>(): Promise<T> {
    return this.get<T>('/settings');
  }

  async updateSettings<T>(settings: T): Promise<void> {
    return this.put('/settings', settings);
  }

  async patchSettings<T>(section: string, data: T): Promise<void> {
    return this.patch(`/settings/${section}`, data);
  }

  async getLoadProfileValues<T>(profileType: string): Promise<T> {
    return this.get<T>(`/settings/loadprofiles/${profileType}/values`);
  }

  // Generic HTTP methods - these do NOT auto-prefix with zone
  // Use these for zone management and other global endpoints
  async httpGet<T>(endpoint: string): Promise<T> {
    return this.get<T>(endpoint);
  }

  /** Binary download (ZIP, PDF) of a global endpoint. */
  async httpGetBlob(endpoint: string, timeoutMs = 120000): Promise<Blob> {
    return this.get<Blob>(endpoint, { responseType: 'blob', timeout: timeoutMs });
  }

  async httpPost<T>(endpoint: string, data?: any): Promise<T> {
    return this.post<T>(endpoint, data);
  }

  async httpPut<T>(endpoint: string, data?: any): Promise<T> {
    return this.put<T>(endpoint, data);
  }

  async httpPatch<T>(endpoint: string, data?: any): Promise<T> {
    return this.patch<T>(endpoint, data);
  }

  async httpDelete<T>(endpoint: string): Promise<T> {
    return this.delete<T>(endpoint);
  }

  // Zone-aware generic HTTP methods
  // Use these when you want automatic zone prefixing
  async zoneGet<T>(endpoint: string, config?: ExpectingRequestConfig): Promise<T> {
    return this.get<T>(this.withZonePrefix(endpoint), config);
  }

  async zonePost<T>(endpoint: string, data?: any): Promise<T> {
    return this.post<T>(this.withZonePrefix(endpoint), data);
  }

  async zonePut<T>(endpoint: string, data?: any): Promise<T> {
    return this.put<T>(this.withZonePrefix(endpoint), data);
  }

  async zonePatch<T>(endpoint: string, data?: any): Promise<T> {
    return this.patch<T>(this.withZonePrefix(endpoint), data);
  }

  async zoneDelete<T>(endpoint: string): Promise<T> {
    return this.delete<T>(this.withZonePrefix(endpoint));
  }
}

// Export singleton instance
export const networkApi = NetworkApiClient.getInstance();
export { NetworkApiClient };
